// START GENAI@CHATGPT4
import { Component, EventEmitter, Fragment, h, Listen, Prop, State, Watch } from '@stencil/core';
import { FormError, FormInfoData, Response } from './vs-standard-form.model';
import {
  AUTH_TYPE, CityClubList, CountryCityList, defaultALPSuccess,
  defaultBaseUrl,
  defaultGenericErrorMessage,
  defaultLoginError,
  defaultSubmitButtonText,
  defaultSuccessMessage,
  defaultTokenMissingErrorMessage,
  defaultVCESValidationEndPoint,
  getApiEndPoints,
  getAuthHeader,
  getHeader,
  WORKFLOW,
} from './vs-standard-form.constant';
import {
  disableSubmitButton,
  fetchApi,
  getFormFieldErrors,
  getFormToken,
  getNonVSFUFileSubmissionPayLoad, getSentosalCancelPayload,
  getSubmissionPayload,
} from './helpers/form.helper';

import { handleErrorResponse, redirectUrlSafe } from './helpers/error.helper';
import {
  getAlignLeftCSS,
  getDataFromPropString,
  isALPWorkflow,
  isDirRtl,
  isRedemptionWorkflow,
  isUnenrollVOPWorkflow
} from '@utils/utils';
import { VsVcesVerificationFormCustomEvent } from '@src/components';
import {
  getReturnUrl,
  getUrlTokenIfAny,
  getUserInfoFromGmap,
  isAuthWorkflow, isSentosaCancelWorkflow, isSentosaWorkflow, isVCESWorkflow,
  mapGmapValueToFormFields,
  preValidateAuthFlow,
  setAuthPath,
} from '@src/workflows/vs-standard-form/helpers/auth.helper';
import { getCookie } from '@src/workflows/vs-standard-form/helpers/cache.helper';
import {
  getVsfuResultPolling,
  populateVsfuUploadContent,
  postVsfuFileUpload,
  VSFU_RESULT,
} from '@src/components/vs-form-builder/components/file-upload/vs-file-upload.helper';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';
import { validateSentosaGolfBooking } from '@src/workflows/vs-sentosa-golf/helpers/form.helper';

@Component({
  tag: 'vs-standard-form',
  styleUrl: './vs-standard-form.scss',
  shadow: false,
})

export class VsStandardForm {

  /**
   * @shortDescription If true, enable the developer mode.
   * @controlName dev
   */
  @Prop() dev: string;

  /**
   @shortDescription The fields to display.
   @controlName fields
   */
  @Prop() fields: string;

  /**
   @shortDescription If true, align the component to the right.
   @controlName right
   */
  @Prop() right: boolean = isDirRtl();

  /**
   @shortDescription The heading of the component.
   @controlName heading
   */
  @Prop() heading: string;

  /**
   @shortDescription The description of the component.
   @controlName description
   */
  @Prop() description: string;

  /**
   @shortDescription The promo ID for the component.
   @controlName promoId
   */
  @Prop() promoId: string;

  /**
   @shortDescription The base URL for the API endpoint.
   @controlName baseUrl
   */
  @Prop() baseUrl: string = defaultBaseUrl;

  /**
   @shortDescription The API endpoint.
   @controlName apiEndPoint
   */
  @Prop() apiEndPoint: string;

  /**
   @shortDescription The API endpoint.
   @controlName fileApiEndPoint
   */
  @Prop() fileApiEndPoint: string;

  /**
   @shortDescription The generic error message to display.
   @controlName genericErrorMessage
   */
  @Prop() genericErrorMessage: string = defaultGenericErrorMessage;

  /**
   @shortDescription The text to display on the submit button.
   @controlName submitButtonText
   */
  @Prop() submitButtonText: string = defaultSubmitButtonText;

  /**
   * @shortDescription promotion locale
   * @controlName locale
   */
  @Prop() locale: string = 'en_sg';

  /**
   @shortDescription The text to display on the reset button.
   @controlName resetButtonText
   */
  @Prop() resetButtonText: string;

  /**
   @shortDescription The index of the component (if there are multiple components on the same page).
   @controlName index
   */
  @Prop() index: string;

  /**
   @shortDescription The data center to use.
   @controlName xDataCenter
   */
  @Prop() xDataCenter = 'OCC0';

  /**
   @shortDescription The success message to display from configuration.
   @controlName successMessageContent
   */
  @Prop() successMessageContent: string;

  /**
   @shortDescription Display label as an eyebrow view for an input field
   @controlName eyebrow
   */
  @Prop() eyebrow: boolean = false;

  /**
   * @shortDescription general config for the form to adopt two columns per row
   * @controlName isTwoColumnsPerRow */
  @Prop() isTwoColumnsPerRow = false;

  /**
   * @shortDescription general config for the form to show the Terms and Conditions page first
   * @controlName showTncPageFirst */
  @Prop() showTncPageFirst = false;

  /**
   * @shortDescription image url next to the form
   * @controlName sideImgUrl */
  @Prop() sideImgUrl: string;

  /**
   * @shortDescription image alt text next to the form
   * @controlName sideImgAltText */
  @Prop() sideImgAltText: string;

  /**
   * @shortDescription define the workflow for submission
   * @controlName workflow */
  @Prop() workflow: string;

  /**
   * @shortDescription token missing error messages
   * @controlName tokenMissingErrorMessage */
  @Prop() tokenMissingErrorMessage: string = defaultTokenMissingErrorMessage;

  /* START: alp workflow related props */
  /**
   * @shortDescription alpSuccessScreenReaderText
   * @controlName alpSuccessScreenReaderText */
  @Prop() alpSuccessScreenReaderText: string;

  /**
   * @shortDescription alpConfirmationHeading
   * @controlName alpConfirmationHeading */
  @Prop() alpConfirmationHeading: string;

  /**
   * @shortDescription alpConfirmationDescription
   * @controlName alpConfirmationDescription */
  @Prop() alpConfirmationDescription: string;

  // START GENAI@GHCOPILOT
  /**
   * @shortDescription alpConfirmationMiddle
   * @controlName alpConfirmationMiddle */
  @Prop() alpConfirmationMiddle: string;

  /**
   * @shortDescription alpConfirmationEnd
   * @controlName alpConfirmationEnd */
  @Prop() alpConfirmationEnd: string;
  // END GENAI@GHCOPILOT

  /**
   * @shortDescription alpConfirmationLinkText
   * @controlName alpConfirmationLinkText */
  @Prop() alpConfirmationLinkText: string;

  /**
   * @shortDescription alpSuccessBinText
   * @controlName alpSuccessBinText */
  @Prop() alpSuccessBinText: string;

  @State() isSuccessALPData = false;
  @State() successALPData = defaultALPSuccess;
  /* END: 9 digit workflow related props */

  /* START: vces workflow related props */
  /**
   * @shortDescription vcesVerificationHeading
   * @controlName vcesVerificationHeading */
  @Prop() vcesVerificationHeading: string;

  /**
   * @shortDescription vcesVerificationDescription
   * @controlName vcesVerificationDescription */
  @Prop() vcesVerificationDescription: string;

  /**
   * @shortDescription vcesVerificationLabel
   * @controlName vcesVerificationLabel */
  @Prop() vcesVerificationLabel: string = 'Enter the 16-digit code';

  /**
   * @shortDescription vcesVerificationErrorMessage
   * @controlName vcesVerificationErrorMessage */
  @Prop() vcesVerificationErrorMessage: string = 'Please enter a valid 16-digit code';

  /**
   * @shortDescription vcesMandatoryErrorMessage
   * @controlName vcesMandatoryErrorMessage */
  @Prop() vcesMandatoryErrorMessage: string = 'Please input a 16-digit code';

  /**
   * @shortDescription Flag to indicate if form should only show VCES verification step
   * @controlName isVCESStepOnly
   * @default false
   */
  @Prop() isVcesStepOnly: boolean = false;

  @State() isVCESVerified = false;

  @State() vcesVerifiedCardNumber: string;
  /* END: vces workflow related props */

  /* START: AUTH Workflow related props */
  /**
   @shortDescription If true, enable authentication for the form
   @controlName isAuth
   */
  @Prop() isAuth: boolean = false;

  // START GENAI@CHATGPT4

  /**
   * @shortDescription The auth page header.
   * @controlName auth-header
   */
  @Prop() authHeader: string = 'auth page header';

  /**
   * @shortDescription The auth page description.
   * @controlName auth-description
   */
  @Prop() authDescription: string = 'auth page description';

  /**
   * @shortDescription The auth page log in text.
   * @controlName auth-login-text
   */
  @Prop() authLoginText: string = 'auth page log in text';

  /**
   * @shortDescription The auth page sign up text.
   * @controlName auth-signup-text
   */
  @Prop() authSignupText: string = 'auth page sign up text';

  /**
   * @shortDescription The auth page message.
   * @controlName auth-message
   */
  @Prop() authMessage: string = 'auth page message';

  /**
   * @shortDescription CSS class for the grid. This is the class that is applied to the component's grid.
   * @controlName formGrid
   */
  @Prop() formGrid: string;

  /**
   @shortDescription The label for the required text field. This text is displayed when a form field is required.
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /**
   * @shortDescription If this campaign contains quiz mechanics, it will require quiz-config
   * @controlName quiz-config
   */
  @Prop() quizConfig: string | undefined;

// START GENAI@GHCOPILOT
  /**
   * @shortDescription If true, the form will submit automatically
   * @controlName auto-submit
   */
  @Prop() autoSubmit: boolean = false;
  // END GENAI@GHCOPILOT

// END GENAI@CHATGPT4

  /**
   * @shortDescription sitekey for hcaptcha
   * @controlName hCaptchaSiteKey */
  @Prop() captchaSitekey!: string;

  /* SENTOSA GOLF Workflow */
  /**
   * @shortDescription align to left
   * @controlName isAlignLeft */
  @Prop() isAlignLeft: boolean = false;

  /**
   * @shortDescription if form should be rendered as two column
   * @controlName isTwoColumnForm */
  @Prop() isTwoColumnForm: boolean = false;

  /**
   * @shortDescription check if golf program is fully booked
   * @controlName checkIfFullyBooked */
  @Prop() checkIfFullyBooked: boolean = false;

  @Prop() fullyBookedMessage: string = 'This golf program has been fully booked.';
  /**
   * @shortDescription The text to display on the Terms and Conditions accept button
   * @controlName tncAcceptBtnText */
  @Prop() tncAcceptBtnText: string = 'Accept';

  /**
   @shortDescription The tncFields to display.
   @controlName tncFields
   */
  @Prop() tncFields: string;

  /**
   @shortDescription flag to indicate if submission requires a modal
   @controlName submitThroughModal
   */
  @Prop() submitThroughModal: boolean = false;

  /**
   @shortDescription Title of the modal
   @controlName modalTitle
   */
  @Prop() modalTitle: string;

  /**
   @shortDescription content of the modal
   @controlName modalContent
   */
  @Prop() modalContent: string;

  /**
   @shortDescription cancel button text for the modal
   @controlName modalCancelText
   */
  @Prop() modalCancelText: string;

  /**
   @shortDescription confirm button text for the modal
   @controlName modalConfirmText
   */
  @Prop() modalConfirmText: string;
  /* END OF SENTOSA GOLF WORKFLOW */

  /**
   @shortDescription inject default values in certain scenarios on submission
   @controlName injectDefaultValues
   */
  @Prop() injectDefaultValues: string;

  @State() authPath = {
    loginUrl: undefined,
    signUpUrl: undefined,
    addCardUrl: undefined,
  };

  /**
   * @shortDescription Emit reset form
   * @controlName resetFormEvent */
    // @ts-ignore
  @Event({
    eventName: 'isSuccessFileUpload',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onResetFormHandler: EventEmitter;

  @State() isGmapLogin: boolean = false;
  @State() hCaptchaSiteKey: string;
  @State() isQuizRender: boolean = false;

  /* START: Sentosa golf states */
  @State() isTncAccepted: boolean;
  @State() ctryCityList: CountryCityList[];
  @State() cityClubList: CityClubList[];

  /* END: Sentosa golf states */

  checkGMAPSession(authPath) {
    if (authPath?.loginUrl) {
      this.isShowForm = false;
      const intervalId = setInterval(() => {
        const isAuthenticated = !!getCookie('gmap_authenticated');
        if (!isAuthenticated) {
          this.clearGmapAuth();
        }
        clearInterval(intervalId);
      }, 60000); // Reading cookies for every 60 seconds. this is a lightweight check. takes <1ms
    }
  }

  clearGmapAuth() {
    this.isGmapLogin = false;
    this.isShowForm = true;
  }

  /* END: AUTH Workflow related props */

  /* START: Default Workflow related props */
  @State() token: string;
  @State() formResponseMessage: string;
  @State() isLoading: boolean;
  @State() formFieldErrorMessages: FormError[] = [];
  @State() apiErrorMessages: string;
  @State() errorCode: string;
  @State() urlToken: string;
  @State() loginRetryCount = 0;
  private MAX_LOGIN_COUNT = 3;
  /**
   * @shortDescription Emit is show form event
   * @controlName isShowFormEvent */
    // @ts-ignore
  @Event({
    eventName: 'isShowFormEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) isShowFormEventHandler: EventEmitter;
  @State() isShowForm: boolean = false;

  @Watch('isShowForm')
  onIsShowFormChange(newValue: boolean) {
    this.isShowFormEventHandler.emit(newValue);
  }

  /**
   * @shortDescription Emit is show form event
   * @controlName isShowFormEvent */
    // @ts-ignore
  @Event({
    eventName: 'formResetEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) formResetEventHandler: EventEmitter;

  /**
   * @shortDescription Emit form submit event when submit button is clicked
   * @controlName vcesVerifiedEvent */
    // @ts-ignore
  @Event({
    eventName: 'vcesVerifiedEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onVcesVerifiedHandler: EventEmitter;

  /**
   * @shortDescription Emit form submit event when submit button is clicked
   * @controlName formSubmitSuccessEvent */
    // @ts-ignore
  @Event({
    eventName: 'formSubmitSuccessEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onFormSubmitSuccessHandler: EventEmitter;

  /**
   * @shortDescription Emit when form string event
   * @controlName fieldStrEvent */
    // @ts-ignore
  @Event({
    eventName: 'fieldStrEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) fieldStrEventHandler: EventEmitter;
  @State() fieldsStr: string;

  @Watch('fieldStr')
  onFieldStrChange(newValue: boolean) {
    this.fieldStrEventHandler.emit(newValue);
  }

  /* END: Default Workflow related props */

  @State() quizToken: string;
  @State() isFullyBooked: boolean;

  // START of File Upload related props
  @State() fileContent;
  @State() isVsfuEnabled: boolean;
  @State() vsfuScanId: string;
  @State() vsfuStatus: string;

  /**
   * @shortDescription Emit upload file in progress event
   * @controlName resetFormEvent */
    // @ts-ignore
  @Event({
    eventName: 'uploadFileInProgressEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) uploadFileInProgressEventHandler: EventEmitter;
  @State() isUploadInProgress: boolean;

  @Watch('isUploadInProgress')
  onIsUploadInProgressChange(newValue: string) {
    const fileUploadStatus = {
      isUploading: newValue,
      status: this.isSuccessFileUpload,
      errorMessage: this.uploadErrorMessage,
    };
    this.uploadFileInProgressEventHandler.emit(fileUploadStatus);
  }

  // END of File Upload related props

  @Listen('resetFormEvent', { target: 'body' })
  resetStandardFormHandler() {
    this.resetState();
    this.formFieldErrorMessages = [];
    this.clearGmapAuth();
  }

  @State() isSuccessFileUpload: boolean = false;
  @State() uploadErrorMessage: string;

  componentWillLoad() {
    this.resetState();
    this.urlToken = getUrlTokenIfAny();
    this.fieldsStr = this.fields;
    this.getHCaptchaKey();
    this.authPath = setAuthPath(this.isAuth, this.workflow, this.locale, getReturnUrl());
    this.loadFormToken().then(() => {
      if (isSentosaWorkflow(this.workflow)) {
        this.initProgramsConfig();
        this.checkIfFullyBooked && this.checkGolfBooking().then();
      }
    });
  }

  getHCaptchaKey() {
    // @ts-ignore
    if (this.captchaSitekey?.length > 0 && !window?.mepaConfig?.isSkipCaptcha) {
      this.hCaptchaSiteKey = this.captchaSitekey;
    } else {
      this.hCaptchaSiteKey = undefined;
    }
  }

  // START GENAI@GHCOPILOT
  /* Step 1: load JWT token from backend, check status of the campaign */
  private async loadFormToken(isVCES: boolean = false) {
    if (this.loginRetryCount > this.MAX_LOGIN_COUNT && !isVCES) {
      return;
    }
    this.isLoading = true;
    this.isShowForm = true;
    try {
      const data = await getFormToken(this.baseUrl, this.promoId, this.xDataCenter, getHeader);
      this.handleFormTokenResponse(data);
    } catch (e) {
      this.handleFormTokenError();
    } finally {
      this.resetLoadingState();
    }
  }

  private handleFormTokenResponse(data: any) {
    if (data.success) {
      this.processSuccessfulFormTokenResponse(data);
      this.checkGMAPSession(this.authPath);
      if (isAuthWorkflow(this.workflow)) {
        this.preValidateAuthPromo(this.token).then();
      }
    } else {
      this.processErrorFormTokenResponse(data);
    }
  }

  private processSuccessfulFormTokenResponse(data: any) {
    this.token = data?.token;
    this.isVsfuEnabled = data?.config?.vsfu;
    this.isShowForm = !isAuthWorkflow(this.workflow);
  }

  private processErrorFormTokenResponse(data: any) {
    this.isShowForm = false;
    if (data?.redirect) {
      redirectUrlSafe(data?.message);
      return;
    }
    this.formResponseMessage = data?.message || defaultLoginError;
  }

  private handleFormTokenError() {
    this.isShowForm = false;
    this.formResponseMessage = defaultLoginError;
  }

  private resetLoadingState() {
    this.isLoading = false;
    disableSubmitButton(false);
    this.displayFormIfDevTrue();
  }

  // END GENAI@GHCOPILOT

  private displayFormIfDevTrue() {
    if (this.isDevMode()) {
      this.isShowForm = true;
    }
  }

  // STEP 2: if Authentication flow, get GMAP user data
  /* START of Authentication pre-validation */

  // START GENAI@GHCOPILOT
  async preValidateAuthPromo(token: string) {
    this.isLoading = true;
    this.isShowForm = false;
    try {
      const preValidateResponse = await preValidateAuthFlow(token, AUTH_TYPE, this.baseUrl, this.xDataCenter, null);
      await this.handlePreValidateResponse(preValidateResponse);
    } catch (e) {
      this.handleGmapAuthError(e);
    } finally {
      this.isLoading = false;
    }
  }

  private async handlePreValidateResponse(preValidateResponse: any) {
    this.isGmapLogin = preValidateResponse?.success;
    if (this.isGmapLogin) {
      await this.handleGmapLogin();
    } else {
      this.handleGmapAuthError(preValidateResponse);
    }
  }

  private async handleGmapLogin() {
    try {
      const gmapPromoInfoResponse = await this.getAndMapUserInfoToForm();
      this.fieldsStr = mapGmapValueToFormFields(gmapPromoInfoResponse, this.fields);
      this.isShowForm = true;
    } catch (error) {
      this.handleGmapAuthError(error);
    }
  }

  // END GENAI@GHCOPILOT

  // START GENAI@GHCOPILOT
  private handleGmapAuthError(error: any) {
    this.clearGmapAuth();
    this.handleError(error);
  }

  // END GENAI@GHCOPILOT

  async getAndMapUserInfoToForm() {
    return await getUserInfoFromGmap(this.baseUrl);
  }

  /* END of Authentication pre-validation */

  // STEP 3: file upload submission with vsfu or normal form submission
  private onFormLoading = async (event) => {
    this.isLoading = event.detail;
    if (!this.isLoading) {
      this.reloadCaptcha();
    }
  };

  /* START of File Uploaded feature */
  private onFileUpload = async (fileUploadEvent) => {
    this.fileContent = fileUploadEvent.detail;
    if (this.isVsfuEnabled && this.fileContent) {
      this.vsfuScanId = null;
      this.vsfuStatus = null;
      this.processVsfuUpload(this.fileContent);
    }
  };

  processVsfuUpload(fileContent) {
    this.isUploadInProgress = true;
    postVsfuFileUpload(this.baseUrl, this.token, this.xDataCenter, populateVsfuUploadContent(fileContent))
      .then(response => {
        this.handleVsfuUploadResponse(response);
      }).catch(e => {
      this.handleError(e);
    });
  }

  // START GENAI@CHATGPT4
  handleVsfuUploadResponse(response) {
    if (!response) return;

    if (!response.success) {
      this.isSuccessFileUpload = false;
      this.uploadErrorMessage = response.message;
      this.isUploadInProgress = false;
      this.handleError(response);
      return;
    }

    if (!response.vsfuId) {
      console.log('VSFU ID is null.');
      this.handleError(response);
      return;
    }

    this.vsfuScanId = response.vsfuId;
    // Create a new poll request
    let retryTimes = 1;
    this.retrieveVsfuResult(retryTimes);
  }

  handleVSFUFileUploadRetry(retryTimes, response) {
    if (retryTimes <= 5) {
      const parent = this;
      const vsfuResultPollFn = setTimeout(() => {
        clearTimeout(vsfuResultPollFn);
        parent.retrieveVsfuResult(retryTimes);
      }, 15000);
    } else {
      this.appendUploadErrorMessage(response?.message);
      console.log('VSFU fails after 5 times.');
      throw new Error(this.genericErrorMessage);
    }
  }

  handleVSFUFileUploadSuccess(response) {
    this.isSuccessFileUpload = true;
    this.isUploadInProgress = false;
    this.apiErrorMessages = null;
    this.vsfuStatus = response.vsfuStatus;
    if (this.vsfuStatus === VSFU_RESULT.BLOCKED) {
      this.onResetFormHandler.emit(true);
    }
  }

  handleVSFUFileUploadError(response) {
    this.appendUploadErrorMessage(response?.message);
    console.log('VSFU fails with exception.');
    this.handleError(response);
  }

  retrieveVsfuResult(retryTimes) {
    console.log('getting result: ' + retryTimes);

    retryTimes = retryTimes + 1;
    getVsfuResultPolling(this.baseUrl, this.token, this.xDataCenter, this.vsfuScanId)
      .then(response => {
        if (!response.success) {
          this.handleVSFUFileUploadError(response);
          return;
        }
        if (!response.vsfuId
          || response.vsfuId !== this.vsfuScanId
          || response.vsfuStatus === VSFU_RESULT.PROCESSING) {
          this.handleVSFUFileUploadRetry(retryTimes, response);
          return;
        }
        this.handleVSFUFileUploadSuccess(response);
      }).catch(() => {
      this.appendUploadErrorMessage(null);
      console.log('VSFU fails with exception.');
      throw new Error(this.genericErrorMessage);
    });
  }

// END GENAI@CHATGPT4

  // START GENAI@CHATGPT4
  appendUploadErrorMessage(uploadErrorMessage: string) {
    this.isSuccessFileUpload = false;
    this.uploadErrorMessage = uploadErrorMessage;
    this.isUploadInProgress = false;
  }

  // END GENAI@CHATGPT4
  /* END of File Uploaded feature */

  private isLackingUrlToken(): boolean {
    return ((isRedemptionWorkflow(this.workflow) || isUnenrollVOPWorkflow(this.workflow)) && !this.urlToken);
  }

  private isDevMode = (): boolean => this.dev === 'true';

  // START GENAI@CHATGPT4
  private handleSubmitOnDevMode = (): void => {
    if (this.isDevMode()) {
      this.isLoading = false;
      this.onFormSubmitSuccessHandler.emit('success');
      if (this.quizConfig?.length > 0) {
        this.isShowForm = true;
        this.isQuizRender = true;
        this.quizToken = 'quizToken';
      } else {
        this.isShowForm = false;
        this.formResponseMessage = "[Placeholder for success message returned from api]";
      }
    }
  };
  // END GENAI@CHATGPT4

  private onTncAcceptSubmit = async (formData: FormInfoData) => {
    if (this.isDevMode()) {
      this.isTncAccepted = true;
      return;
    }
    this.formFieldErrorMessages = getFormFieldErrors(formData);
    this.isTncAccepted = this.formFieldErrorMessages.length === 0;
  };

  /* START of Form Submission */
  private onFormSubmit = async (formData: FormInfoData) => {
    const isNonVSFUFileUpload = !this.isVsfuEnabled && this.fileContent;
    this.isLoading = true;
    let formFinalPayload: any | FormData;
    if (isNonVSFUFileUpload) {
      formFinalPayload = getNonVSFUFileSubmissionPayLoad(formData, this.promoId,
        this.fileContent, this.vcesVerifiedCardNumber, this.urlToken);
    } else if (isSentosaCancelWorkflow(this.workflow)) {
      formFinalPayload = getSentosalCancelPayload(formData);
    } else {
      formFinalPayload = getSubmissionPayload(formData, this.promoId,
        this.urlToken, this.workflow, this.vsfuScanId,
        this.vcesVerifiedCardNumber);
    }
    this.formFieldErrorMessages = getFormFieldErrors(formData);
    if (this.injectDefaultValues) {
      const valuesToInject = getDataFromPropString(this.injectDefaultValues);
      console.log('formFinalPayload', formFinalPayload)
      formFinalPayload.form = [...formFinalPayload.form, ...valuesToInject];
      console.log('formFinalPayload after', formFinalPayload)
    }
    if (this.isDevMode()) {
      console.log('formData', formData)
      this.handleSubmitOnDevMode();
      return;
    }
    // START GENAI@CHATGPT4
    if (this.isLackingUrlToken()) {
      this.apiErrorMessages = this.tokenMissingErrorMessage;
      this.isLoading = false;
      disableSubmitButton(false);
      return;
    } else if (this.formFieldErrorMessages.length > 0 || !formFinalPayload) {
      this.isLoading = false;
      disableSubmitButton(false);
      return;
    }
    // END GENAI@CHATGPT4

    await this.handleSubmitAPICall(isNonVSFUFileUpload, formFinalPayload, isAuthWorkflow(this.workflow));
  };

  // START GENAI@CHATGPT4

  getEndPoint(isNonVSFUFileUpload: boolean, apiEndPoint: string, fileApiEndPoint: string): string {
    let endPoint = apiEndPoint;
    if (isNonVSFUFileUpload) {
      endPoint = fileApiEndPoint;
    }
    return this.baseUrl + endPoint;
  }

  getAppropriateHeader(isAuthFlow: boolean, formFinalPayload: any, isNonVSFUFileUpload: boolean): any {
    return isAuthFlow
      ? getHeader(this.xDataCenter, formFinalPayload, this.token, isNonVSFUFileUpload)
      : getAuthHeader(this.xDataCenter, formFinalPayload, this.token, isNonVSFUFileUpload, AUTH_TYPE);
  }

  handleSuccessResponse(responseJson: Response) {
    this.isShowForm = false;
    this.isLoading = false;
    this.onFormSubmitSuccessHandler.emit('success');
    if (responseJson.redirect) {
      /* START GENAI@GHCOPILOT */
      const redirectUrl = responseJson?.successMessage;
      if (redirectUrl === 'quiz/index.html') {
        this.isQuizRender = true;
        this.quizToken = responseJson?.token;
      } else {
        redirectUrlSafe(redirectUrl);
      }
      return;
      /* END GENAI@GHCOPILOT */
    }
    /* START GENAI@GHCOPILOT */
    if (responseJson.errors && responseJson.errors.length > 0) {
      this.handleError(responseJson, true);
      return;
    }
    /* END GENAI@GHCOPILOT */
    if (!this.handleALPSuccess(responseJson)) {
      this.getSuccessMessageOnFormSubmission(responseJson);
    }
  }

  private getSuccessMessageOnFormSubmission(responseJson: Response | null): void {
    this.formResponseMessage = this.successMessageContent || responseJson?.successMessage || defaultSuccessMessage;
  }

  async handleSubmitAPICall(isNonVSFUFileUpload: boolean, formFinalPayload: any, isAuthFlow: boolean) {
    try {
      //get apiEndpoints for both default form submission and file form submission
      let { apiEndPoint, fileApiEndPoint } = getApiEndPoints(this.workflow);
      if (this.apiEndPoint) {
        apiEndPoint = this.apiEndPoint;
      }
      if (this.fileApiEndPoint) {
        fileApiEndPoint = this.fileApiEndPoint;
      }

      //get the URL endpoint
      const urlEndPoint = this.getEndPoint(isNonVSFUFileUpload, apiEndPoint, fileApiEndPoint);

      //get header for the respective workflow form submission
      const header = this.getAppropriateHeader(isAuthFlow, formFinalPayload, isNonVSFUFileUpload);

      // await fetch
      const responseJson: Response = await fetchApi(urlEndPoint, header);

      //handle success and error according to response
      if (!responseJson.success) {
        this.handleError(responseJson, true);
      } else {
        this.handleSuccessResponse(responseJson);
      }
    } catch (e) {
      this.handleError(e, true);
    }
  }

  // END GENAI@CHATGPT4

  /* END of Form Submission */

  /* START of Error handling */

  // START GENAI@CHATGPT4
  resetStateWithError() {
    this.resetState();
    this.apiErrorMessages = this.genericErrorMessage;
  }

  setErrorDetails(response: Response) {
    const errorResponse
      = handleErrorResponse(response, this.genericErrorMessage);
    this.apiErrorMessages = errorResponse.apiErrorMessages;
    this.errorCode = errorResponse.errorCode;
    if (this.workflow !== WORKFLOW.AUTHENTICATION) {
      this.formResponseMessage = errorResponse.apiErrorMessages;
    }
  }

  handleFormError() {
    this.loginRetryCount++;
    if (this.loginRetryCount >= this.MAX_LOGIN_COUNT) {
      this.isLoading = false;
      disableSubmitButton(false);
    } else {
      this.loadFormToken().then();
    }
  }

  handleError = (response: Response, isFormError: boolean = false): void => {
    console.log('Error response: ', response);
    if (!response) {
      this.resetStateWithError();
      return;
    }

    // handle redirection
    if (response.redirect) {
      /* START GENAI@GHCOPILOT */
      const redirectUrl = response.errors[0].message;
      redirectUrlSafe(redirectUrl);
      return;
      /* END GENAI@GHCOPILOT */
    }

    // reset form if there's file upload to prevent limbo vsfu state due to successful upload but failed form submission
    if (this.isVsfuEnabled && this.fileContent) {
      this.resetState();
      this.formResetEventHandler.emit(true);
    }
    /* START GENAI@GHCOPILOT */
    this.workflow === WORKFLOW.VOP_RELOADABLE ? this.handleVopReloadableError(response) : this.setErrorDetails(response);
    /* END GENAI@GHCOPILOT */
    if (isFormError) {
      this.handleFormError();
    }
  };

  handleVopReloadableError(response: Response) {
    if (response.errors) {
      if (response.errors[0]) {
        const redirectUrl = response.errors[0].message;
        if (response.redirect && redirectUrl) {
          redirectUrlSafe(redirectUrl);
        } else {
          this.apiErrorMessages = response.errors[0].message;
          if (response.errors[0].message === 'Card not eligible') {
            this.apiErrorMessages = 'Bitte achten Sie darauf, Ihre Kartennummer korrekt einzugeben. Beachten Sie, dass wir bei dieser Aktion nur Visa Karten einer deutschen Bank akzeptieren. Nicht aufladbare Prepaid-Karten sowie VPAY Karten sind ausgeschlossen.';
          }
          if (response.errors[0].message === 'Card not reloadable') {
            this.apiErrorMessages = 'Bitte achten Sie darauf, Ihre Kartennummer korrekt einzugeben. Beachten Sie, dass wir bei dieser Aktion nur Visa Karten einer deutschen Bank akzeptieren. Nicht aufladbare Prepaid-Karten sowie VPAY Karten sind ausgeschlossen.';
          }
          if (response.errors[0].code) {
            this.errorCode = response.errors[0].code.toString();
          }
        }
      }
    } else if (response.message) {
      this.formResponseMessage = response.message;
      this.apiErrorMessages = response.message;
      if (response.code === 400) {
        this.apiErrorMessages = 'vop exception';
      }
      if (response.message === 'Card not eligible') {
        this.apiErrorMessages = 'Bitte achten Sie darauf, Ihre Kartennummer korrekt einzugeben. Beachten Sie, dass wir bei dieser Aktion nur Visa Karten einer deutschen Bank akzeptieren. Nicht aufladbare Prepaid-Karten sowie VPAY Karten sind ausgeschlossen.';
      }
      if (response.message === 'Card not reloadable') {
        this.apiErrorMessages = 'Bitte achten Sie darauf, Ihre Kartennummer korrekt einzugeben. Beachten Sie, dass wir bei dieser Aktion nur Visa Karten einer deutschen Bank akzeptieren. Nicht aufladbare Prepaid-Karten sowie VPAY Karten sind ausgeschlossen.';
      }
      if (response.message === 'Email has already been unenrolled') {
        this.apiErrorMessages = 'Sie sind bereits von der Aktion abgemeldet.';
      }
      if (response.code === 4104) {
        this.apiErrorMessages = 'Sie sind bereits von der Aktion abgemeldet.';
      }
    }
  }

  private reloadCaptcha = (): void => {
    if (this.hCaptchaSiteKey) {
      const hCaptcha = document.querySelector('vs-hcaptcha');
      hCaptcha?.resetCaptcha();
    }
  };

  private resetState = (): void => {
    this.reloadCaptcha();
    this.vsfuScanId = null;
    this.vsfuStatus = null;
    this.isLoading = false;
    this.formResponseMessage = null;
    this.apiErrorMessages = null;
    this.errorCode = null;
    this.isUploadInProgress = false;
    this.isSuccessFileUpload = false;
    this.isSuccessALPData = false;
    this.uploadErrorMessage = undefined;
    this.isVCESVerified = false;
    this.vcesVerifiedCardNumber = undefined;
    this.successALPData = defaultALPSuccess;
  };

  /* END of Error handling */

  private handleALPSuccess = (response: Response): boolean => {
    if (isALPWorkflow(this.workflow)) {
      if (response?.promotion?.additionalInformation?.MERCHANT_URL) {
        const binResponse = response?.promotion?.additionalInformation;
        this.formResponseMessage = null;
        this.isSuccessALPData = true;
        this.successALPData = {
          merchantUrl: binResponse.MERCHANT_URL,
          cardNum: binResponse.CARD_NUM,
          CardTypeCd: binResponse.CARD_TYPE_CD,
          bin: binResponse.BIN_NO,
          binUrl: !(binResponse?.ENCRYPT === 'true') ? binResponse.BIN_NO : binResponse?.CARD_NO,
        };
        return true;
      }
    }
    return false;
  };

  // Start DOM rendering

  private getPromotionFormDom = () => {
    return <vs-form-builder
      isAlignLeft={this.isAlignLeft}
      id={`standard-form-${this.index}`}
      index={this.index}
      autoSubmit={this.autoSubmit}
      formGrid={this.formGrid}
      requiredText={this.requiredText}
      heading={this.heading}
      description={this.description}
      fields={this.fieldsStr}
      eyebrow={this.eyebrow}
      isTwoColumnsPerRow={this.isTwoColumnsPerRow}
      sideImgUrl={this.sideImgUrl}
      isDefaultShowForm={true}
      ctryCityList={this.ctryCityList}
      cityClubList={this.cityClubList}
      isShowFirstFourCardNumber={isALPWorkflow(this.workflow)}
      sideImgAltText={this.sideImgAltText}
      submitButtonText={this.submitButtonText}
      resetButtonText={this.resetButtonText}
      onFileUploadEvent={this.onFileUpload}
      onFormLoadingEvent={this.onFormLoading}
      onFormSubmitEvent={this.onFormSubmit}
      submitThroughModal={this.submitThroughModal}
      modalTitle={this.modalTitle}
      modalContent={this.modalContent}
      modalConfirmText={this.modalConfirmText}
      modalCancelText={this.modalCancelText}
      captchaSiteKey={this.hCaptchaSiteKey}>
      <div slot='formFieldErrorSlot'>
        {
          this.formFieldErrorMessages
          && this.formFieldErrorMessages.length > 0
          && this.getFormFieldListErrorDom()
        }
      </div>
      <div slot='headingSlot'>
        <slot name='headingSlot' />
      </div>
      <div slot='descriptionSlot'>
        <slot name='descriptionSlot' />
      </div>
      {
        this.formResponseMessage && !this.isShowForm && this.getFormResponseDom()
      }
      <div slot='apiErrorSlot'>
        {
          this.isShowForm && this.apiErrorMessages && this.getAPIResponseErrorDom()
        }
      </div>
      <div slot='footerSlot'>
        <slot name='footerSlot' />
      </div>
      <div slot='inputSlot'>
        <slot name='inputSlot' />
      </div>
    </vs-form-builder>;
  };

  getFormFieldListErrorDom() {
    return <p role='alert' class='vs-col-12 vs-mb-2 vs-text vs-meta errorFormMessage'
              aria-live='polite' id='errorFormMessage'>
      <ul class='vs-error'>
        {this.formFieldErrorMessages.map(error => {
          return (<li><a class='error-link'
                         href={`#${error.formIndex}`} innerHTML={decodeHTMLEntities(error.message)}></a></li>);
        })}
      </ul>
    </p>;
  }

  getAPIResponseErrorDom() {
    return <p role='alert' aria-live='polite'
              class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-1'
              id='genericErrorMessage'>
      <span aria-hidden='true' class='visa-icon-alert' />
      <span innerHTML={this.apiErrorMessages} />
    </p>;
  }

  get vcesVerificationField() {
    return `[
       {"type": "cardInput", "name":"pan", "label":"${this.vcesVerificationLabel}",
       "singleColumn": "true",
       "rules": { "required":"${this.vcesMandatoryErrorMessage}",
                  "cardCheck":"${this.vcesVerificationErrorMessage}",
                  "numeric":"${this.vcesVerificationErrorMessage}"
                  }}
      ]`;
  }


  private getVCESVerificationFormDom = () => {
    return <vs-vces-verification-form
      index={this.index}
      {...(this.isDevMode() && {
        dev: 'true',
      })}
      isAlignLeft={this.isAlignLeft}
      requiredText={this.requiredText}
      formGrid={this.formGrid}
      isTwoColumnForm={this.isTwoColumnForm}
      promoId={this.promoId}
      baseUrl={this.baseUrl}
      token={this.token}
      heading={this.vcesVerificationHeading}
      description={this.vcesVerificationDescription}
      apiEndPoint={defaultVCESValidationEndPoint}
      xDataCenter={this.xDataCenter}
      eyebrow={this.eyebrow}
      genericErrorMessage={this.genericErrorMessage}
      fields={this.vcesVerificationField}
      submitButtonText={this.submitButtonText}
      resetButtonText={this.resetButtonText}
      sideImgUrl={this.sideImgUrl}
      sideImgAltText={this.sideImgAltText}
      onVcesFormLoadingEvent={this.onFormLoading}
      onVCESVerified={(event: VsVcesVerificationFormCustomEvent<string>) => {
        this.formVerifiedSuccess(event.detail);
      }}
      onResetVCESFormEvent={() => {
        this.loadFormToken(true).then();
      }}
      captchaSitekey={this.hCaptchaSiteKey}
    >
      <div slot='formFieldErrorSlot'>
        {
          this.formFieldErrorMessages
          && this.formFieldErrorMessages.length > 0
          && this.getFormFieldListErrorDom()
        }
      </div>
      <div slot='headingSlot'>
        <slot name='headingSlot' />
      </div>
      <div slot='descriptionSlot'>
        <slot name='descriptionSlot' />
      </div>
      {
        this.formResponseMessage && !this.isShowForm && this.getFormResponseDom()
      }
      <div slot='apiErrorSlot'>
        {
          this.isShowForm && this.apiErrorMessages && this.getAPIResponseErrorDom()
        }
      </div>
      {this.isShowForm &&
        <div slot='additionalContentSlot'>
          <slot name='additionalContentSlot' />
        </div>
      }
      <div slot='footerSlot'>
        <slot name='footerSlot' />
      </div>
      <div slot='inputSlot'>
        <slot name='inputSlot' />
      </div>
    </vs-vces-verification-form>;
  };

  // START GENAI@GHCOPILOT
  private formVerifiedSuccess = (vcesVerifiedCardNumber: string) => {
    this.vcesVerifiedCardNumber = vcesVerifiedCardNumber;

    if (!this.autoSubmit) {
      this.isLoading = false;
    }

    if (this.isVcesStepOnly) {
      this.isShowForm = false;
      this.getSuccessMessageOnFormSubmission(null);
      return;
    }

    this.isVCESVerified = true;
    this.onVcesVerifiedHandler.emit({ verified: this.isVCESVerified });
  };
  // END GENAI@GHCOPILOT


  private getPreAuthDom = () => {
    return <div class='vs-mx-auto vs-col-lg-9 vs-col-12'>
      <div class='vs-mx-auto vs-col-lg-9 vs-col-12'>
        <h1 class={`vs-h2 vs-mt-3 ${getAlignLeftCSS(this.isAlignLeft)}`}>{this.authHeader}</h1>
        <hr class='vs-line-break' />
        {this.authDescription?.length > 0 &&
          <p class='vs-text dont-break-out' innerHTML={this.authDescription} />}
        <a href={this.authPath?.signUpUrl}
           class='vs-btn vs-btn-default vs-btn-icon vs-btn-block vs-mb-1'>{this.authSignupText}</a>
        <a href={this.authPath?.loginUrl}
           class='vs-btn vs-btn-primary vs-btn-icon vs-btn-block'>{this.authLoginText}</a>
        {this.authMessage?.length > 0
          && <p class={`vs-text ${getAlignLeftCSS(this.isAlignLeft)} vs-pt-2`} innerHTML={this.authMessage} />}
      </div>
    </div>;
  };

  // START GENAI@CHATGPT4
  private getALPSuccessDom = () => {
    return <vs-alp-success
      alpSuccessScreenReaderText={this.alpSuccessScreenReaderText}
      alpConfirmationHeading={this.alpConfirmationHeading}
      alpConfirmationDescription={this.alpConfirmationDescription}
      alpConfirmationLinkText={this.alpConfirmationLinkText}
      alpConfirmationMiddle={this.alpConfirmationMiddle}
      alpConfirmationEnd={this.alpConfirmationEnd}
      alpSuccessBinText={this.alpSuccessBinText}
      successALPData={this.successALPData} />;
  };
  // END GENAI@CHATGPT4

  private getFormResponseDom = () => {
    return <div slot='successSlot'>
      {!this.successMessageContent &&
      <p class={`vs-h3 vs-p-4 success-message ${getAlignLeftCSS(this.isAlignLeft)}`}
         innerHTML={decodeHTMLEntities(this.formResponseMessage)} />
      }
      {
        this.successMessageContent && <div class={`success-message`} innerHTML={this.successMessageContent} />
      }

    </div>;
  };

  private getLoadingDom = () => {
    return <div role='region' class='vs-loading-overlay' aria-live='polite'>
      <div class='vs-loading-wrapper'>
        <div class='spinner'>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </div>;
  };

  isQuizDomRender = (): boolean => {
    return this.isQuizRender && this.quizConfig?.length > 0 && this.quizToken?.length > 0;
  };

  getQuizDom = () => {
    return <vs-quiz
      dev={this.dev}
      quiz-config={this.quizConfig}
      promo-id={this.promoId}
      token={this.quizToken}
      x-data-center={this.xDataCenter}
      base-url={this.baseUrl}
      onQuizLoadingEvent={this.onFormLoading}
      generic-error-message={this.genericErrorMessage} />;
  };

  // START GENAI@CHATGPT4
  private renderForm = (): Element => {
    if (this.isQuizDomRender()) {
      return this.getQuizDom();
    } else {
      return this.getPromotionFormDom();
    }
  };
  // END GENAI@GHCOPILOT

  // START GENAI@GHCOPILOT
  initProgramsConfig() {
    //@ts-ignore
    const programConfig = window?.programConfig;
    if (programConfig) {
      this.ctryCityList = programConfig.ctryCityList ?? this.ctryCityList;
      this.cityClubList = programConfig.cityClubList ?? this.cityClubList;
    }
  }

  // END GENAI@GHCOPILOT

  // START GENAI@GHCOPILOT
  private async checkGolfBooking() {
    this.isLoading = true;
    try {
      const bookingData = await validateSentosaGolfBooking(this.baseUrl, this.xDataCenter, this.token);
      this.isFullyBooked = !((bookingData?.valid) ?? false);
    } catch (e) {
      this.isFullyBooked = true;
      console.log('error when checking if golf program is fully booked', e);
    } finally {
      this.isLoading = false;
    }
  }

  // END GENAI@GHCOPILOT

  /* START GENAI@GHCOPILOT */
  checkIfDefaultWorkflow() {
    return !isALPWorkflow(this.workflow) && !isAuthWorkflow(this.workflow) && !isVCESWorkflow(this.workflow) && !isSentosaWorkflow(this.workflow) && !isSentosaCancelWorkflow(this.workflow);
  }

  /* END GENAI@GHCOPILOT */

  /* START GENAI@GHCOPILOT */

  /* This function handles forms that require an additional step before the actual form,
   * such as in Auth or VCES workflows.
   * It validates the campaign date during the loadFormToken step.
   * If the campaign hasn't started or has ended, it displays an error message.
   * Otherwise, it shows the pre-step DOM during the campaign period.
   */
  private renderPreFormStepOrResponseMessage(preFormStepDom: () => JSX.Element): JSX.Element {
    return <Fragment>
      {this.isShowForm ? preFormStepDom : (this.formResponseMessage && this.getFormResponseDom())}
    </Fragment>;
  }

  /* END GENAI@GHCOPILOT */

  private renderSentosaGolfForm() {
    if (this.isFullyBooked) {
      return null;
    }
    return (
      <Fragment>
        {(this.showTncPageFirst && !this.isTncAccepted) ? this.renderTncPage() : this.renderVCESForm()}
      </Fragment>
    );
  }

  private renderVCESForm() {
    return <Fragment> {
      this.isVCESVerified ? this.renderForm()
        : this.renderPreFormStepOrResponseMessage(this.getVCESVerificationFormDom())
    }
    </Fragment>;
  }

  private renderTncPage() {
    return <vs-form-builder
      {...(this.isDevMode() && {
        dev: 'true',
      })}
      isAlignLeft={this.isAlignLeft}
      formGrid={this.formGrid}
      index={'tncFormPage'}
      captchaSiteKey={null}
      fields={this.tncFields}
      isDefaultShowForm={true}
      submitButtonText={this.tncAcceptBtnText}
      onFormSubmitEvent={this.onTncAcceptSubmit}>
      <div slot='preFormTncSlot'>
        <slot name='preFormTncSlot' />
      </div>
      <div slot='formFieldErrorSlot'>
        {
          this.formFieldErrorMessages
          && this.formFieldErrorMessages.length > 0
          && this.getFormFieldListErrorDom()
        }
      </div>
      {
        this.formResponseMessage && !this.isShowForm && this.getFormResponseDom()
      }
      <div slot='apiErrorSlot'>
        {
          this.isShowForm && this.apiErrorMessages && this.getAPIResponseErrorDom()
        }
      </div>
    </vs-form-builder>;
  }

  render() {
    return (
      <Fragment>
        {
          this.isFullyBooked && <div innerHTML={this.fullyBookedMessage} />
        }
        {
          this.isLoading && this.getLoadingDom()
        }
        {
          <Fragment>
            {isALPWorkflow(this.workflow) && (this.isSuccessALPData ? this.getALPSuccessDom() : this.renderForm())}
            {isAuthWorkflow(this.workflow) && (this.isGmapLogin ? this.renderForm() : this.renderPreFormStepOrResponseMessage(this.getPreAuthDom()))}
            {isVCESWorkflow(this.workflow) && this.renderVCESForm()}
            {isSentosaWorkflow(this.workflow) && this.renderSentosaGolfForm()}
            {isSentosaCancelWorkflow(this.workflow) && this.renderForm()}
            {/* Default form */}
            {this.checkIfDefaultWorkflow() && this.renderForm()}
          </Fragment>
        }
      </Fragment>
    );
  }

  // END DOM rendering
}

